import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import Modal from 'flarum/common/components/Modal';
import Button from 'flarum/common/components/Button';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import debugHelper from '../../admin/utils/debugHelper';

// #366 / Phase 1 i18n — Flarum 1.8 translator.trans() 返回 vnode array 而非 string，
// 直接当 JSX children 会触发 mithril onupdate normalize path 上的 `replace undefined`
// warning。t() helper flatten 为 string 避开。
// WHY: see memory/flarum-1.8-pitfalls.md 坑 B
function t(key) {
  const result = app.translator.trans(key);
  return Array.isArray(result) ? result.join('') : result;
}

// 比例列表 (aspectRatio → 宽:高, null 表示自由)
const ASPECT_RATIOS = [{
  key: 'free',
  label: () => t('ziven-dress-up.forum.image_crop.ratio_free'),
  value: null
}, {
  key: '1:1',
  label: '1:1',
  value: 1
}, {
  key: '4:3',
  label: '4:3',
  value: 4 / 3
}, {
  key: '3:4',
  label: '3:4',
  value: 3 / 4
}, {
  key: '16:9',
  label: '16:9',
  value: 16 / 9
}];

/**
 * v0.4.7 #365 — 图片剪裁 Modal (方案 B)
 *
 * 触发流程:
 *   选文件 → AvatarModal/EquipmentSelectModal.processFile 校验通过
 *   → 检查 localStorage 'seedream.cropEnabled' (默认 ON)
 *   → ON: 弹本 modal, 用户拖选 + 应用 → onConfirm(croppedBlob, croppedDataUrl)
 *   → 父组件用 cropped 数据继续原上传流程
 *   → OFF: 直接走原流程
 *
 * 关键: 用 content() 方法, 不能用 view() (v0.4.7a #364 教训)
 *   view() 覆盖父类 → .Modal.Modal--normal.ImageCropModal 容器不存在
 *   → className() 返回的 'ImageCropModal Modal--normal' 永远没应用
 *   → 所有 .ImageCropModal xxx CSS 不命中
 *
 * Props (attrs):
 *   - imageDataUrl: string — 要剪裁的图片 dataURL
 *   - onConfirm: (blob: Blob, dataUrl: string) => void — 父组件回调
 *   - onCancel: () => void (可选) — 取消回调, 默认关闭 modal
 */
export default class ImageCropModal extends Modal {
  oninit(vnode) {
    super.oninit(vnode);

    // 状态
    this.imageDataUrl = this.attrs.imageDataUrl || '';
    this.loading = true; // 加载图片
    this.aspectRatio = '1:1'; // 默认 1:1
    this.scale = 1;
    this.rotation = 0;

    // 选区 (相对 stage 容器坐标, 百分比表示 0~1)
    // 初始 60%×70% 居中 (高度按比例锁)
    this.crop = {
      x: 0.2,
      y: 0.2,
      w: 0.6,
      h: 0.6
    };
    // 修正初始 (1:1) — h = w
    this.crop.h = 0.6;

    // 拖拽
    this.dragMode = null; // 'move' | 'resize-tl' | 'resize-tr' | 'resize-bl' | 'resize-br'
    this.dragStart = null; // { crop, mouseX, mouseY, stageRect }

    // 镜像 / 准备中的 image 对象 (在 loadImage 后填充)
    this.image = null;

    // Stage DOM ref
    this.stageRef = null;
    this.selectionRef = null;
    this.handleRefs = null; // { tl, tr, bl, br } — 由 mithril ref callback 填充

    this.loadImage();
  }
  className() {
    return 'ImageCropModal Modal--normal';
  }
  title() {
    return t('ziven-dress-up.forum.image_crop.title');
  }

  /**
   * 加载图片 (dataURL → Image 对象)
   */
  loadImage() {
    if (!this.imageDataUrl) {
      this.loading = false;
      return;
    }
    const img = new Image();
    img.onload = () => {
      this.image = img;
      this.loading = false;
      m.redraw();
    };
    img.onerror = () => {
      debugHelper.error('[ImageCrop] Failed to load image');
      this.loading = false;
      m.redraw();
    };
    img.src = this.imageDataUrl;
  }

  /**
   * 获取当前选区的真实像素坐标 (用于 canvas 剪裁)
   * 选区 x/y/w/h 是相对 stage 的 0~1 比例
   */
  getCropPixelRect(stageW, stageH) {
    // 计算 image 在 stage 中的实际显示尺寸 (object-fit: contain)
    const imgW = this.image.naturalWidth;
    const imgH = this.image.naturalHeight;
    const imgRatio = imgW / imgH;
    const stageRatio = stageW / stageH;
    let drawW, drawH;
    if (imgRatio > stageRatio) {
      drawW = stageW;
      drawH = stageW / imgRatio;
    } else {
      drawH = stageH;
      drawW = stageH * imgRatio;
    }
    const offsetX = (stageW - drawW) / 2;
    const offsetY = (stageH - drawH) / 2;
    return {
      x: offsetX + this.crop.x * stageW,
      y: offsetY + this.crop.y * stageH,
      w: this.crop.w * stageW,
      h: this.crop.h * stageH,
      // 映射回原图坐标 (考虑 scale + rotation)
      // 当前 v0.4.7 #365 MVP: 不应用 scale/rotation 到剪裁结果 (避免越界复杂逻辑)
      // scale/rotation 仅作 UI 视觉预览, 应用剪裁时按原图等比例剪
      //
      // 关键: 视觉选区是相对 stage 的 0~1, 但图片以 object-fit: contain 渲染
      // (image area = [offsetX, offsetX+drawW]), 必须先把视觉坐标平移到 image area
      // (减 offsetX/offsetY), 再按 (imgW/drawW) 缩放回原图坐标系。
      // 漏减 offsetX 是 v0.4.7 字母版本遗留 bug, 当 imgRatio < stageRatio (高比宽瘦
      // 的图放进宽 stage) 时视觉选区跟剪裁结果错位。
      //
      // Math.max(0, ...) 兜底: 选区超出 image 实际渲染区 (落在 letterbox 黑边) 时
      // srcX/srcY 可能是负数, canvas drawImage 自动 clip 到 image bounds
      srcX: Math.max(0, (this.crop.x * stageW - offsetX) * (imgW / drawW)),
      srcY: Math.max(0, (this.crop.y * stageH - offsetY) * (imgH / drawH)),
      srcW: this.crop.w * stageW * (imgW / drawW),
      srcH: this.crop.h * stageH * (imgH / drawH)
    };
  }

  /**
   * 应用剪裁 — canvas 截取 + toBlob
   */
  applyCrop() {
    if (!this.image || !this.stageRef) return;
    const stageRect = this.stageRef.getBoundingClientRect();
    const stageW = stageRect.width;
    const stageH = stageRect.height;
    const rect = this.getCropPixelRect(stageW, stageH);

    // 创建 canvas (按选区尺寸, 限制 max 1024 避免过大)
    const maxDim = 1024;
    let outW = rect.srcW;
    let outH = rect.srcH;
    if (outW > maxDim || outH > maxDim) {
      const r = Math.min(maxDim / outW, maxDim / outH);
      outW = Math.round(outW * r);
      outH = Math.round(outH * r);
    }
    const canvas = document.createElement('canvas');
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext('2d');

    // 高质量缩放
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(this.image, rect.srcX, rect.srcY, rect.srcW, rect.srcH,
    // 源图选区
    0, 0, outW, outH // 画到 canvas
    );
    canvas.toBlob(blob => {
      if (!blob) {
        debugHelper.error('[ImageCrop] canvas.toBlob failed');
        return;
      }
      // 生成 dataURL 给预览 (FileReader)
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result;
        if (typeof this.attrs.onConfirm === 'function') {
          this.attrs.onConfirm(blob, dataUrl);
        }
        app.modal.close();
      };
      reader.onerror = () => {
        debugHelper.error('[ImageCrop] FileReader failed');
      };
      reader.readAsDataURL(blob);
    }, 'image/jpeg', 0.92);
  }

  /**
   * 切换比例 pill
   *
   * v0.4.7c 返工 (辉哥 [P1]):
   *   原算法 3 个 bug (L196-226 v0.4.7b):
   *     1. value 是 image 像素宽高比 (如 4/3), 不是 stage 容器宽高比
   *     2. 假设 stage 是正方形算 newH, 但 stage 实际是 4:3/16:9/1:1 容器
   *     3. newH > 1 强制限没考虑 stage 实际宽高
   *   新算法按 stage 实际宽高 stageW/stageH 算归一化选区, 在 stage 内最大化
   *   (选区视觉像素宽高比 = value, 不溢出 stage)
   *
   * v0.4.7e 返工 (辉哥 [P1]):
   *   辉哥测出"切比例填满整 stage, 不像默认 50-60%" 体验问题
   *   修法: 取 stage 短边 60% 为基准, 按 value 横竖分两支计算 (限制不溢出 stage)
   *   保持选区视觉比例 = value, 大小 ≈ stage 短边 60%
   */
  setAspectRatio(key) {
    const def = ASPECT_RATIOS.find(r => r.key === key);
    if (!def) return;
    this.aspectRatio = key;
    if (def.value === null) {
      // 自由 — 保持当前
      return;
    }

    // 防御: stageRef 还没就绪 (极端情况) — 跳过切比例, 等下次 pill click
    if (!this.stageRef) return;

    // stage 实际像素宽高 (Flarum Modal 自适应, 不是正方形)
    const stageRect = this.stageRef.getBoundingClientRect();
    const stageW = stageRect.width;
    const stageH = stageRect.height;
    if (stageW <= 0 || stageH <= 0) return;
    const value = def.value; // 目标视觉宽高比 w/h (如 4/3)

    // 取 stage 短边 60% 为基准
    const baseSide = Math.min(stageW, stageH) * 0.6;

    // 按 value 横竖分两支: 横宽选区 (value>=1) / 竖高选区 (value<1)
    let newW_visual, newH_visual;
    if (value >= 1) {
      // 横宽选区 (1:1, 4:3, 16:9)
      newW_visual = baseSide * value;
      newH_visual = baseSide;
    } else {
      // 竖高选区 (3:4)
      newW_visual = baseSide;
      newH_visual = baseSide / value;
    }

    // 限制不溢出 stage (保护: 小 stage 时缩放)
    if (newW_visual > stageW) {
      newW_visual = stageW;
      newH_visual = stageW / value;
    }
    if (newH_visual > stageH) {
      newH_visual = stageH;
      newW_visual = stageH * value;
    }

    // 归一化到 stage [0, 1]
    const newW_norm = newW_visual / stageW;
    const newH_norm = newH_visual / stageH;

    // 保持中心 (cx, cy 不变)
    const cx = this.crop.x + this.crop.w / 2;
    const cy = this.crop.y + this.crop.h / 2;
    this.crop.x = Math.max(0, Math.min(cx - newW_norm / 2, 1 - newW_norm));
    this.crop.y = Math.max(0, Math.min(cy - newH_norm / 2, 1 - newH_norm));
    this.crop.w = newW_norm;
    this.crop.h = newH_norm;
    m.redraw();
  }

  /**
   * 工具按钮: 缩小 / 放大 / 左旋 / 右旋 / 重置
   */
  zoomIn() {
    this.scale = Math.min(3.0, this.scale + 0.1);
    m.redraw();
  }
  zoomOut() {
    this.scale = Math.max(0.5, this.scale - 0.1);
    m.redraw();
  }
  rotateLeft() {
    this.rotation = (this.rotation - 90) % 360;
    m.redraw();
  }
  rotateRight() {
    this.rotation = (this.rotation + 90) % 360;
    m.redraw();
  }
  reset() {
    this.scale = 1;
    this.rotation = 0;
    this.crop = {
      x: 0.2,
      y: 0.2,
      w: 0.6,
      h: 0.6
    };
    // 按当前 aspectRatio 修正
    const def = ASPECT_RATIOS.find(r => r.key === this.aspectRatio);
    if (def && def.value) {
      this.crop.h = this.crop.w / def.value;
    }
    m.redraw();
  }

  /**
   * 拖拽 — mousedown 启动
   */
  onMouseDown(mode, e) {
    e.preventDefault();
    e.stopPropagation();
    if (!this.stageRef) return;
    const stageRect = this.stageRef.getBoundingClientRect();
    this.dragMode = mode;
    this.dragStart = {
      crop: _objectSpread({}, this.crop),
      mouseX: e.clientX,
      mouseY: e.clientY,
      stageW: stageRect.width,
      stageH: stageRect.height
    };
    // 绑定全局事件 (移到 body 才能跨元素跟踪)
    document.addEventListener('pointermove', this.boundMouseMove);
    document.addEventListener('pointerup', this.boundMouseUp);
  }

  /**
   * 拖拽 — mousemove 更新 crop
   */
  onMouseMove(e) {
    if (!this.dragMode || !this.dragStart) return;
    e.preventDefault();
    const dx = (e.clientX - this.dragStart.mouseX) / this.dragStart.stageW;
    const dy = (e.clientY - this.dragStart.mouseY) / this.dragStart.stageH;
    const startCrop = this.dragStart.crop;
    const newCrop = _objectSpread({}, startCrop);
    if (this.dragMode === 'move') {
      // 整体平移 — 限制在 stage 内
      newCrop.x = Math.max(0, Math.min(1 - startCrop.w, startCrop.x + dx));
      newCrop.y = Math.max(0, Math.min(1 - startCrop.h, startCrop.y + dy));
    } else {
      // 角缩放 — 4 角分别处理
      const def = ASPECT_RATIOS.find(r => r.key === this.aspectRatio);
      const aspectRatio = def && def.value ? def.value : null;
      let newX = startCrop.x;
      let newY = startCrop.y;
      let newW = startCrop.w;
      let newH = startCrop.h;
      if (this.dragMode === 'resize-tl') {
        newX = Math.min(startCrop.x + startCrop.w - 0.05, startCrop.x + dx);
        newY = Math.min(startCrop.y + startCrop.h - 0.05, startCrop.y + dy);
        newW = startCrop.w - (newX - startCrop.x);
        newH = startCrop.h - (newY - startCrop.y);
      } else if (this.dragMode === 'resize-tr') {
        newY = Math.min(startCrop.y + startCrop.h - 0.05, startCrop.y + dy);
        newW = Math.max(0.05, startCrop.w + dx);
        newH = startCrop.h - (newY - startCrop.y);
      } else if (this.dragMode === 'resize-bl') {
        newX = Math.min(startCrop.x + startCrop.w - 0.05, startCrop.x + dx);
        newW = startCrop.w - (newX - startCrop.x);
        newH = Math.max(0.05, startCrop.h + dy);
      } else if (this.dragMode === 'resize-br') {
        newW = Math.max(0.05, startCrop.w + dx);
        newH = Math.max(0.05, startCrop.h + dy);
      }

      // 锁定比例 (如果不是 free)
      if (aspectRatio) {
        // 维持 width/height 比例
        if (newW / newH > aspectRatio) {
          // 太宽 — 按高度推算 width
          newW = newH * aspectRatio;
          if (this.dragMode === 'resize-tl' || this.dragMode === 'resize-bl') {
            newX = startCrop.x + startCrop.w - newW;
          }
        } else {
          // 太高 — 按宽度推算 height
          newH = newW / aspectRatio;
          if (this.dragMode === 'resize-tl' || this.dragMode === 'resize-tr') {
            newY = startCrop.y + startCrop.h - newH;
          }
        }
      }

      // 限制在 stage 内
      newW = Math.max(0.05, Math.min(1, newW));
      newH = Math.max(0.05, Math.min(1, newH));
      newX = Math.max(0, Math.min(1 - newW, newX));
      newY = Math.max(0, Math.min(1 - newH, newY));
      newCrop.x = newX;
      newCrop.y = newY;
      newCrop.w = newW;
      newCrop.h = newH;
    }
    this.crop = newCrop;
    m.redraw();
  }

  /**
   * 拖拽 — mouseup 结束
   */
  onMouseUp() {
    this.dragMode = null;
    this.dragStart = null;
    document.removeEventListener('pointermove', this.boundMouseMove);
    document.removeEventListener('pointerup', this.boundMouseUp);
  }

  /**
   * 取消 — 关闭 modal, 调 onCancel (如果有)
   */
  cancel() {
    if (typeof this.attrs.onCancel === 'function') {
      this.attrs.onCancel();
    }
    app.modal.close();
  }

  /**
   * content() — #365 关键: 用 content() 不要用 view() (避免 #364 v0.4.7a 样式失效)
   */
  content() {
    if (this.loading) {
      return m("div", {
        class: "Modal-body ImageCropModal-body"
      }, m("div", {
        class: "image-crop-loading"
      }, m(LoadingIndicator, {
        size: "medium"
      }), m("span", null, t('ziven-dress-up.forum.image_crop.loading'))));
    }
    if (!this.image) {
      return m("div", {
        class: "Modal-body ImageCropModal-body"
      }, m("div", {
        class: "image-crop-error"
      }, m("i", {
        class: "fas fa-exclamation-triangle"
      }), m("p", null, t('ziven-dress-up.forum.image_crop.load_error'))));
    }
    const selectionStyle = {
      left: this.crop.x * 100 + '%',
      top: this.crop.y * 100 + '%',
      width: this.crop.w * 100 + '%',
      height: this.crop.h * 100 + '%'
    };

    // 图片视觉 (scale + rotation) — 不影响实际剪裁坐标
    const imgStyle = {
      transform: "scale(".concat(this.scale, ") rotate(").concat(this.rotation, "deg)"),
      transformOrigin: 'center center',
      transition: 'transform 0.18s ease'
    };
    return m("div", {
      class: "Modal-body ImageCropModal-body"
    }, m("div", {
      class: "crop-stage",
      ref: el => this.stageRef = el
    }, m("img", {
      src: this.imageDataUrl,
      alt: "crop target",
      style: imgStyle,
      draggable: false
    }), m("div", {
      class: "crop-selection",
      ref: el => this.selectionRef = el,
      style: selectionStyle
    }, m("div", {
      class: "crop-handle tl",
      ref: el => this.handleRefs = _objectSpread(_objectSpread({}, this.handleRefs || {}), {}, {
        tl: el
      })
    }), m("div", {
      class: "crop-handle tr",
      ref: el => this.handleRefs = _objectSpread(_objectSpread({}, this.handleRefs || {}), {}, {
        tr: el
      })
    }), m("div", {
      class: "crop-handle bl",
      ref: el => this.handleRefs = _objectSpread(_objectSpread({}, this.handleRefs || {}), {}, {
        bl: el
      })
    }), m("div", {
      class: "crop-handle br",
      ref: el => this.handleRefs = _objectSpread(_objectSpread({}, this.handleRefs || {}), {}, {
        br: el
      })
    }), m("div", {
      class: "crop-rule h-top"
    }), m("div", {
      class: "crop-rule h-mid"
    }), m("div", {
      class: "crop-rule h-bot"
    }), m("div", {
      class: "crop-rule v-l"
    }), m("div", {
      class: "crop-rule v-m"
    }), m("div", {
      class: "crop-rule v-r"
    }))), m("div", {
      class: "crop-toolbar"
    }, m("div", {
      class: "tool-group"
    }, m("button", {
      class: "tool-btn",
      onclick: () => this.zoomOut(),
      title: t('ziven-dress-up.forum.image_crop.tool_zoom_out')
    }, m("i", {
      class: "fas fa-search-minus"
    })), m("button", {
      class: "tool-btn",
      onclick: () => this.zoomIn(),
      title: t('ziven-dress-up.forum.image_crop.tool_zoom_in')
    }, m("i", {
      class: "fas fa-search-plus"
    })), m("button", {
      class: "tool-btn",
      onclick: () => this.rotateLeft(),
      title: t('ziven-dress-up.forum.image_crop.tool_rotate_left')
    }, m("i", {
      class: "fas fa-undo"
    })), m("button", {
      class: "tool-btn",
      onclick: () => this.rotateRight(),
      title: t('ziven-dress-up.forum.image_crop.tool_rotate_right')
    }, m("i", {
      class: "fas fa-redo"
    })), m("button", {
      class: "tool-btn",
      onclick: () => this.reset(),
      title: t('ziven-dress-up.forum.image_crop.tool_reset')
    }, m("i", {
      class: "fas fa-sync-alt"
    }))), m("div", {
      class: "tool-group"
    }, ASPECT_RATIOS.map(r => m("button", {
      key: r.key,
      class: 'aspect-pill' + (this.aspectRatio === r.key ? ' active' : ''),
      onclick: () => this.setAspectRatio(r.key),
      type: "button"
    }, typeof r.label === 'function' ? r.label() : r.label)))), m("div", {
      class: "crop-actions"
    }, m("button", {
      class: "btn-crop btn-cancel",
      onclick: () => this.cancel(),
      type: "button"
    }, t('ziven-dress-up.forum.image_crop.cancel')), m("button", {
      class: "btn-crop btn-confirm",
      onclick: () => this.applyCrop(),
      type: "button"
    }, m("i", {
      class: "fas fa-crop-alt"
    }), ' ' + t('ziven-dress-up.forum.image_crop.apply'))));
  }
  oncreate(vnode) {
    super.oncreate(vnode);

    // 绑定全局 mousemove / mouseup (移到 body 才能跨元素跟踪)
    this.boundMouseMove = this.onMouseMove.bind(this);
    this.boundMouseUp = this.onMouseUp.bind(this);

    // 关键修复 (辉哥 22:02 亲测 bug) — mithril 2.x 不代理 mousedown/mousemove/mouseup
    // 原 onmousedown={...} JSX 属性被 mithril 静默丢弃, 拖拽完全失效
    // 解决: 在 oncreate 用原生 addEventListener 绑到 stage + 4 个 handle
    // 不动 onConfirm/onCancel 签名 (AvatarModal 依赖)
    // 不用改 #366 LandingPage (辉哥正在测)
    // 上一次 mithril redraw 之后 stageRef/handleRefs/selectionRef 才会有 DOM, 用 requestAnimationFrame 等下一帧
    // v0.4.7e 辉哥 [P1]: initDefaultCrop 在 attachDragHandlers 末尾调 (那时 stageRef 已 set)
    requestAnimationFrame(() => this.attachDragHandlers());
  }

  /**
   * 初始化默认选区 — 按 stage 实际宽高算 1:1 选区 (短边 60% 居中)
   *
   * v0.4.7e 辉哥 [P1]:
   *   原 oninit 用占位 crop {0.2, 0.2, 0.6, 0.6}, 但 crop 是归一化 [0,1],
   *   视觉 = (0.6*stageW) × (0.6*stageH), stage 不是正方形时视觉不是 1:1.
   *   修法: 在 attachDragHandlers 末尾调 (那时 stageRef 已通过 querySelector set),
   *   按 stage 实际宽高算 1:1 选区 (取短边 60% 作为正方形边长, 居中)
   *   只初始化一次 (cropInitialized 标志), 用户拖动后不会再覆盖
   */
  initDefaultCrop() {
    if (this.cropInitialized) return; // 只初始化一次, 用户拖动后保留
    if (!this.stageRef) return;
    const stageRect = this.stageRef.getBoundingClientRect();
    const stageW = stageRect.width;
    const stageH = stageRect.height;
    if (stageW <= 0 || stageH <= 0) return;

    // 1:1 选区 = stage 短边 60% 居中
    const side = Math.min(stageW, stageH) * 0.6;
    this.crop.x = (stageW - side) / 2 / stageW;
    this.crop.y = (stageH - side) / 2 / stageH;
    this.crop.w = side / stageW;
    this.crop.h = side / stageH;
    this.cropInitialized = true;
    m.redraw();
  }

  /**
   * 重写 hide() — 让关闭按钮 (X / ESC / 背景点击) 也触发 onCancel
   *
   * v0.4.7e 辉哥 [P1]:
   *   Flarum Modal 默认 hide() 不触发 onCancel, 只有关闭按钮点"取消"按钮才走 onCancel
   *   (cancel() 显式调用 onCancel + app.modal.close()).
   *   但 X / ESC / 背景点击是 hide(), 会绕过 onCancel 导致父 modal 上传框不清空.
   *   修法: 重写 hide() 先调 onCancel 再 super.hide()
   *
   *   注意: applyCrop() 用 app.modal.close() 直接关, 不走 this.hide(),
   *         所以确认按钮不会误触发 onCancel (与期望一致)
   */
  hide() {
    if (typeof this.attrs.onCancel === 'function') {
      this.attrs.onCancel();
    }
    super.hide();
  }

  /**
   * 绑定原生 mousedown 事件到 .crop-stage + 4 个 .crop-handle
   * (因为 mithril 2.x 不代理 mousedown 事件, 必须在 oncreate 用 addEventListener)
   *
   * 关键发现 (辉哥 22:02 亲测 bug debug):
   *   - ref callback (ref={el => this.stageRef = el}) 在 oncreate 时不保证已 fire
   *   - 多次 RAF + onupdate 都不一定 fire (Flarum stackModal=true 隐藏 modal 不重渲 ref)
   *   - 解决: 不依赖 ref, 直接 querySelector('.crop-stage') 等 — 元素在 DOM 中
   */
  attachDragHandlers() {
    if (!this.element) return;

    // 用 querySelector 找元素 (不依赖 ref callback — mithril 2.x stackModal=true
    // 隐藏 modal 的 ref callback 在 oncreate 时不可靠, 但 DOM 元素已经在)
    // 用 this.element 作为根容器
    const root = this.element;
    const stageEl = root.querySelector('.crop-stage');
    const selectionEl = root.querySelector('.crop-selection');
    const handleEls = {
      tl: root.querySelector('.crop-handle.tl'),
      tr: root.querySelector('.crop-handle.tr'),
      bl: root.querySelector('.crop-handle.bl'),
      br: root.querySelector('.crop-handle.br')
    };

    // 同步更新 this.stageRef 等 (供 applyCrop 用)
    this.stageRef = stageEl;
    this.selectionRef = selectionEl;
    this.handleRefs = handleEls;
    if (!stageEl) return;

    // v0.4.7e 辉哥 [P1]: stageRef 已就绪, 算默认 1:1 选区 (短边 60% 居中)
    // initDefaultCrop 内部 cropInitialized 标志防止覆盖用户拖动
    this.initDefaultCrop();

    // 防止重复绑定
    if (this.boundStageMouseDown) {
      return; // 已绑定, 跳过
    }

    // stage: 整体平移 (点在 stage 空白处触发 move)
    this.boundStageMouseDown = e => {
      // 只在 mousedown 直接点在 stage (非 selection 子元素) 上时触发 move
      if (e.target === stageEl || e.target.tagName === 'IMG') {
        e.preventDefault();
        e.stopPropagation();
        this.onMouseDown('move', e);
      }
    };
    stageEl.addEventListener('pointerdown', this.boundStageMouseDown);

    // selection: 点选区内部 (非 corner handle) 时也触发 move (整体平移)
    if (selectionEl) {
      this.boundSelectionMouseDown = e => {
        // 如果点在 corner handle 上 — 让 handle listener 处理 (会 stopPropagation 阻止冒泡)
        if (e.target.classList.contains('crop-handle')) {
          return;
        }
        // 否则 (点在选区内部) — 触发 move
        e.preventDefault();
        e.stopPropagation();
        this.onMouseDown('move', e);
      };
      selectionEl.addEventListener('pointerdown', this.boundSelectionMouseDown);
    }

    // 4 个 corner handles: 缩放
    this.boundHandleMouseDowns = {};
    ['tl', 'tr', 'bl', 'br'].forEach(corner => {
      const el = handleEls[corner];
      if (!el) return;
      const handler = e => {
        e.preventDefault();
        e.stopPropagation();
        this.onMouseDown('resize-' + corner, e);
      };
      el.addEventListener('pointerdown', handler);
      this.boundHandleMouseDowns[corner] = {
        el,
        handler
      };
    });
  }

  /**
   * 监听 mithril redraw, 如果 DOM 已挂但 ref 还没填 (m.redraw race), 重新 attach
   * mithril 在 ref callback 中填 this.handleRefs / stageRef / selectionRef
   * 第一次 view() 后这些是 null, oncreate 后是最新 DOM
   * 但 stackModal=true 时, mithril 可能先 oncreate 后才 mount DOM
   * 所以 RAF + onupdate 双重保险
   */
  onupdate(vnode) {
    super.onupdate(vnode);
    if (!this.boundStageMouseDown) {
      requestAnimationFrame(() => this.attachDragHandlers());
    }
  }
  onremove() {
    // 清理全局 mousemove/mouseup
    if (this.boundMouseMove) document.removeEventListener('pointermove', this.boundMouseMove);
    if (this.boundMouseUp) document.removeEventListener('pointerup', this.boundMouseUp);
    // 清理 stage/selection/handle 原生 mousedown
    if (this.boundStageMouseDown && this.stageRef) {
      this.stageRef.removeEventListener('pointerdown', this.boundStageMouseDown);
    }
    if (this.boundSelectionMouseDown && this.selectionRef) {
      this.selectionRef.removeEventListener('pointerdown', this.boundSelectionMouseDown);
    }
    if (this.boundHandleMouseDowns) {
      Object.values(this.boundHandleMouseDowns).forEach(_ref => {
        let el = _ref.el,
          handler = _ref.handler;
        el.removeEventListener('pointerdown', handler);
      });
    }
    super.onremove();
  }
}
_defineProperty(ImageCropModal, "isDismissibleViaBackdropClick", false);
_defineProperty(ImageCropModal, "isDismissibleViaCloseButton", true);
flarum.reg.add('ziiven-dress-up', 'forum/components/ImageCropModal', ImageCropModal);